import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { pendingVisitId, withSpan, type BoardItem } from '@/lib/boardItems';
import type { Job } from './useDispatchBoard';

/**
 * Where a card is being put: who has it, and when. All three are stated.
 *
 * `scheduledStart: null` takes it off the calendar — for a visit card that is
 * REMOVING THE VISIT (Kyle, 2026-09-25): on a three-day repipe only that day
 * goes, and a job's last visit going makes it Unscheduled, exactly as the job
 * page's Remove does.
 */
export interface Placement {
  item: BoardItem;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  assignedUserId: string | null;
  /** For the optimistic card only: the technician's name, so a lead that follows a visit reads right before the refetch. */
  assignedUserName: string | null;
}

/**
 * Which endpoint a placement goes to — the one decision the request and the
 * optimistic patch must make identically, so it is made once.
 *
 * - A job with no visits (or only the optimistic one) is placed with
 *   `/schedule`, which gives it its first.
 * - ⚠️ Taking off a job's ONLY visit is `/schedule` too, not `DELETE`. Both
 *   remove the visit, but `DELETE` never moves the lead, so the job was left
 *   Unscheduled with a lead — in no column and not in the rail, gone from
 *   the board. `/schedule` sets the lead from the placement, so "take it off,
 *   unassigned" lands in the rail as it did before visits (found in review,
 *   M2-52).
 * - Taking off one of SEVERAL visits is `DELETE` (Kyle, 2026-09-25: only that
 *   day goes; `/schedule` refuses a job with several visits anyway).
 * - Anything else moves the visit: `PUT`.
 */
export function routeFor(placement: Pick<Placement, 'item' | 'scheduledStart'>): 'schedule' | 'remove' | 'move' {
  if (placement.item.visitId === null) return 'schedule';
  if (placement.scheduledStart === null) return placement.item.visitCount <= 1 ? 'schedule' : 'remove';
  return 'move';
}

/**
 * Moves a card on the board (M1-11, and per visit since M2-52), optimistically.
 *
 * The card lands where it was dropped immediately and is put back if the
 * server refuses. That ordering is the point: a dispatcher moving twenty jobs
 * before eight o'clock cannot wait on a round trip for each one, and a board
 * that lurches when the response arrives is worse than one that never moved.
 *
 * <b>Three routes, one gesture.</b> A visit card moves through
 * `PUT /jobs/{id}/visits/{visitId}` (only that visit — Tuesday moves, Monday
 * and Wednesday stay) or is taken off with `DELETE`. A job with no visits yet
 * — the rail's unscheduled section — is placed with `/schedule`, which gives
 * it its first. The optimistic patch mirrors whichever the server will do,
 * down to the lead following the only visit and the status the first and
 * last visits derive, because a card that changes once optimistically and
 * again when the refetch lands is the flicker that makes people stop trusting
 * the screen.
 *
 * ⚠️ The rollback restores a SNAPSHOT of the whole list, not the single job.
 * Two moves can be in flight at once — that is the normal case when someone is
 * working quickly — and putting one job back by remembering its old value
 * would also silently undo any later move that touched it.
 */
export function usePlaceJob(queryKey: readonly unknown[]) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (placement: Placement) => {
      const { item } = placement;
      const body = {
        scheduledStart: placement.scheduledStart,
        scheduledEnd: placement.scheduledEnd,
        assignedUserId: placement.assignedUserId,
      };

      const route = routeFor(placement);

      // The `=== null` halves only narrow the types for the compiler: `routeFor`
      // has already decided, and never picks another route for them.
      if (route === 'schedule' || item.visitId === null) {
        const { data, response } = await apiClient.POST('/api/v1/jobs/{id}/schedule', {
          params: { path: { id: item.jobId } },
          body,
        });
        if (!response.ok || !data) {
          throw new Error(`POST /api/v1/jobs/${item.jobId}/schedule failed: ${response.status}`);
        }
        return data;
      }

      if (route === 'remove' || placement.scheduledStart === null) {
        const { data, response } = await apiClient.DELETE('/api/v1/jobs/{jobId}/visits/{visitId}', {
          params: { path: { jobId: item.jobId, visitId: item.visitId } },
        });
        if (!response.ok || !data) {
          throw new Error(`DELETE /api/v1/jobs/${item.jobId}/visits/${item.visitId} failed: ${response.status}`);
        }
        return data;
      }

      const { data, response } = await apiClient.PUT('/api/v1/jobs/{jobId}/visits/{visitId}', {
        params: { path: { jobId: item.jobId, visitId: item.visitId } },
        body: { ...body, scheduledStart: placement.scheduledStart },
      });
      if (!response.ok || !data) {
        throw new Error(`PUT /api/v1/jobs/${item.jobId}/visits/${item.visitId} failed: ${response.status}`);
      }
      return data;
    },

    onMutate: async (placement) => {
      // Without this, a refetch already in flight can land after the
      // optimistic write and overwrite it with the pre-move data — the card
      // would visibly jump back and then forward again.
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Job[]>(queryKey);

      queryClient.setQueryData<Job[]>(queryKey, (jobs) =>
        jobs?.map((job) => (job.id === placement.item.jobId ? placed(job, placement) : job)),
      );

      return { previous };
    },

    onError: (_error, _placement, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },

    onSettled: () => {
      // Refetch either way. On success the server may have derived something
      // the optimistic patch did not model; on failure this re-reads the truth
      // rather than trusting the restored snapshot indefinitely.
      //
      // EVERY job query, not just this board's (found in review, M2-52): a
      // moved visit changes the job's visits, span and, on a one-visit job,
      // its lead, and the job's page, the job list and the OTHER board view
      // (the week, from the day) all show them. At the app's 30-second
      // staleTime, invalidating only this key left the job's page listing
      // the visit where it used to be. The job page's own visit writes
      // (`useJobVisitMutations`) invalidate `['jobs']` for the same reason;
      // the board's key is under it.
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
}

/**
 * The job as the server is about to return it after this placement — the
 * same rules, so the optimistic card and the refetched one agree. Exported
 * for its own unit tests; the board only reaches it through the mutation.
 */
export function placed(job: Job, placement: Placement): Job {
  const { item, scheduledStart, scheduledEnd, assignedUserId, assignedUserName } = placement;
  const technician = { assignedUserId, assignedUserName };

  // Through `/schedule`: a job with no visits gets one (and the lead); taken
  // off — nothing to take, or its only visit — it has none, and the lead is
  // whoever the placement names.
  const route = routeFor(placement);
  if (route === 'schedule') {
    const visits =
      scheduledStart === null
        ? []
        : [
            {
              id: pendingVisitId(job.id),
              scheduledStart,
              scheduledEnd,
              ...technician,
              // M2-21: the visit it moves keeps its bounce marker until the
              // server's answer replaces it.
              emailBounced: job.visits[0]?.emailBounced ?? false,
            },
          ];
    return withSpan({ ...job, ...technician, visits, status: nextStatus(job.status, scheduledStart !== null) });
  }

  // One of several visits taken off the calendar: that visit goes, and the
  // lead stays — `DELETE` never moves it.
  if (route === 'remove' || scheduledStart === null) {
    const visits = job.visits.filter((visit) => visit.id !== item.visitId);
    return withSpan({
      ...job,
      visits,
      status: visits.length === 0 && job.status === 'Scheduled' ? 'Unscheduled' : job.status,
    });
  }

  // A visit moved: only that visit changes, and on a one-visit job a new
  // technician takes the lead with them.
  const before = job.visits.find((visit) => visit.id === item.visitId);
  const visits = job.visits.map((visit) =>
    visit.id === item.visitId ? { ...visit, scheduledStart, scheduledEnd, ...technician } : visit,
  );
  const leadFollows = job.visits.length === 1 && before?.assignedUserId !== assignedUserId;
  return withSpan({ ...job, ...(leadFollows ? technician : {}), visits });
}

/**
 * The same derivation `/schedule` performs, and it has to stay that way — if
 * the two drift, the card changes status once optimistically and again when
 * the refetch lands.
 */
function nextStatus(current: string, isScheduled: boolean): string {
  if (isScheduled && current === 'Unscheduled') {
    return 'Scheduled';
  }
  if (!isScheduled && current === 'Scheduled') {
    return 'Unscheduled';
  }
  return current;
}
